import { useEffect, useRef, useState } from "react";

/**
 * DER SPRUNG auf einen Listeneintrag (Spec „Sprünge“) — die EINE Mechanik für den Reiter „Dateien“ und die
 * Lieferantenreiter (Final-Fixrunde Lieferanten, A-W2): Steht der Eintrag mit `[attribute="id"]` in der Liste, rollt er
 * in die Mitte und bekommt den Fokus — genau EINMAL je Kennung, sonst risse jedes spätere Neuladen der Liste den Fokus
 * an sich; ein Sprung auf eine andere Kennung derselben Liste gilt wieder. Kein eigenes Aufleuchten: Der Fokus ist die
 * Markierung.
 *
 *  - `focus` (Selektor im Eintrag) nennt, was den Fokus bekommt — in den Lieferantenreitern der Aufklappknopf der
 *    Zeile („‹Titel› aufklappen“); ohne Angabe der Eintrag selbst (er braucht dann `tabIndex={-1}`).
 *  - `open` läuft beim Treffer VOR dem Fokus (die Zeile aufschlagen); Rollen und Fokus folgen nach dem Neuzeichnen.
 *  - `waitForEntry`: Ohne Angabe verbraucht der Sprung die erste geladene Liste, auch ohne Treffer (Reiter „Dateien“:
 *    ein inzwischen gelöschter Eintrag tut nichts weiter). Mit Angabe wartet er auf den Eintrag — die
 *    Lieferantenreiter zeigen ihn womöglich erst, nachdem sein Unternehmen zugeschaltet ist.
 *
 * Hat der Benutzer bis dahin selbst etwas fokussiert (ein Feld, einen Knopf), bleiben Fokus und Rolllage, wo sie sind.
 * Frei ist der Fokus auf `body` und auf einem Behälter des Eintrags (dem Panel, das ihn nach dem Reiterwechsel hält,
 * KundenAkte.tsx). Gesucht wird im gezeichneten Dokument nach dem Attributwert, nicht über einen Selektor mit der
 * Kennung darin.
 */
export function useJumpFocus(attribute: string, id: string | null | undefined, items: readonly unknown[] | null,
  o: { focus?: string; open?: (id: string) => void; waitForEntry?: boolean } = {}): void {
  const done = useRef<string | null>(null);
  const [landing, setLanding] = useState<string | null>(null);
  const find = (key: string): HTMLElement | undefined => Array.from(document.querySelectorAll<HTMLElement>(`[${attribute}]`))
    .find((el) => el.getAttribute(attribute) === key);
  useEffect(() => {
    if (id === undefined || id === null || items === null || done.current === id) return;
    const entry = find(id);
    if (entry === undefined && o.waitForEntry === true) return;
    done.current = id;
    if (entry === undefined) return;
    o.open?.(id);
    setLanding(id);
    // `open` ist ein Setzer des Aufrufers; neu nur bei neuer Kennung oder neuer Liste.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attribute, id, items]);
  useEffect(() => {
    if (landing === null) return;
    setLanding(null);
    const entry = find(landing);
    const active = document.activeElement;
    if (entry === undefined || (active !== null && !active.contains(entry))) return;
    const target = o.focus === undefined ? entry : entry.querySelector<HTMLElement>(o.focus) ?? entry;
    entry.scrollIntoView?.({ block: "center" });
    target.focus({ preventScroll: true });   // gerollt hat schon scrollIntoView, ein zweites Rollen korrigierte browserabhängig nach
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landing]);
}
